import { useState, useRef, useEffect } from "react";
import { useSpatial } from "./SpatialContext";
import { parseBrainDump } from "../services/brainDumpService";
import ClaudeService from "../services/claudeService";
import TypewriterText from "./TypewriterText";
import { createLumenSessionSync } from "../services/lumenSessionService";

// ═══════════════════════════════════════════════════════════════
// BRAIN DUMP MODAL — NeuroQuest
// Two modes: Brain Dump (AI parse) + Manual Builder
// Shows parsed hierarchy in text for human-in-the-loop review
// User confirms with "Go Spatial" to build the world
// ═══════════════════════════════════════════════════════════════

const TEAL = "#4DB8B5";
const DARK = "#0F172A";
const PANEL = "#FFFFFF";
const TEXT = "#1E293B";
const MUTED = "#64748B";
const BORDER = "rgba(0,0,0,0.08)";

// ── Drag-to-reorder tree ──
function DraggableList({ items, parentId, level, onReorder, renderItem }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const onDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver  = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const onDrop      = (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) onReorder(parentId, dragIdx, idx, level);
    setDragIdx(null); setOverIdx(null);
  };
  const onDragEnd   = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <div>
      {items.map((item, idx) => (
        <div key={item.id}
          draggable
          onDragStart={e => onDragStart(e, idx)}
          onDragOver={e => onDragOver(e, idx)}
          onDrop={e => onDrop(e, idx)}
          onDragEnd={onDragEnd}
          className="transition-[border-color] duration-100"
          style={{
            opacity: dragIdx === idx ? 0.4 : 1,
            borderTop: overIdx === idx && dragIdx !== idx ? "2px solid #4DB8B5" : "2px solid transparent",
          }}
        >
          {renderItem(item, idx)}
        </div>
      ))}
    </div>
  );
}

// ── Render hierarchy as readable text ──
function CollapsibleSection({ defaultOpen = true, children, header }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <div
        onClick={() => setOpen(o => !o)}
        className="cursor-pointer flex items-center gap-1 mb-[2px] w-full"
      >
        <span
          className="text-[8px] text-slate-300 transition-transform duration-150 inline-block"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        >▶</span>
        {header}
      </div>
      {open && children}
    </div>
  );
}

function HierarchyText({ world, onRename, onDelete, onReorder, onSelectBuilding, selectedBuilding, isMerge, newNodeIds }) {
  const isNew = (id) => !isMerge || (newNodeIds && newNodeIds.has(id));
  const hasNew = (nodes) => nodes?.some(n => isNew(n.id) || hasNew(n.islands) || hasNew(n.buildings) || hasNew(n.floors) || hasNew(n.rooms));

  // In merge mode, only show districts/islands/buildings that contain new nodes
  const visibleDistricts = isMerge
    ? world.districts.filter(d => isNew(d.id) || hasNew(d.islands))
    : world.districts;

  return (
    <div className="font-['DM_Sans']">
      {visibleDistricts.map(district => {
        const visibleIslands = isMerge ? (district.islands || []).filter(i => isNew(i.id) || hasNew(i.buildings)) : (district.islands || []);
        return (
          <div key={district.id} className="mb-4">
            <CollapsibleSection defaultOpen={true} header={
              <HierarchyRow node={district} level={0} icon="🌍" onRename={onRename} onDelete={onDelete} isNew={isNew(district.id)} />
            }>
              <DraggableList items={visibleIslands} parentId={district.id} level={0} onReorder={onReorder}
                renderItem={(island) => {
                  const visibleBuildings = isMerge ? (island.buildings || []).filter(b => isNew(b.id) || hasNew(b.floors)) : (island.buildings || []);
                  return (
                    <CollapsibleSection defaultOpen={true} header={
                      <HierarchyRow node={island} level={1} icon="🏝" onRename={onRename} onDelete={onDelete} isNew={isNew(island.id)} />
                    }>
                      <DraggableList items={visibleBuildings} parentId={island.id} level={1} onReorder={onReorder}
                        renderItem={(building) => (
                          <CollapsibleSection defaultOpen={true} header={
                            <HierarchyRow node={building} level={2} icon="🏢" onRename={onRename} onDelete={onDelete}
                              isSelected={selectedBuilding?.id === building.id}
                              onSelect={() => onSelectBuilding && onSelectBuilding(selectedBuilding?.id === building.id ? null : building)}
                              isNew={isNew(building.id)} />
                          }>
                            <DraggableList items={building.floors || []} parentId={building.id} level={2} onReorder={onReorder}
                              renderItem={(floor) => (
                                <CollapsibleSection defaultOpen={true} header={
                                  <HierarchyRow node={floor} level={3} icon="▤" onRename={onRename} onDelete={onDelete} isNew={isNew(floor.id)} />
                                }>
                                  <DraggableList items={floor.rooms || []} parentId={floor.id} level={3} onReorder={onReorder}
                                    renderItem={(room) => (
                                      <HierarchyRow key={room.id} node={room} level={4} icon="🚪" onRename={onRename} onDelete={onDelete} isNew={isNew(room.id)} />
                                    )} />
                                </CollapsibleSection>
                              )} />
                          </CollapsibleSection>
                        )} />
                    </CollapsibleSection>
                  );
                }} />
            </CollapsibleSection>
          </div>
        );
      })}
    </div>
  );
}

function HierarchyRow({ node, level, icon, onRename, onDelete, isSelected, onSelect, isNew }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(node.name);

  const indent = level * 20;
  const colors = ["#4DB8B5", "#A78BFA", "#3B82F6", "#22C55E", "#EAB308"];
  const color = colors[level] || "#94A3B8";

  return (
    <div
      className="flex items-center gap-2 mb-1 rounded-[5px] pr-1 pt-[2px] pb-[2px] transition-[background] duration-100"
      style={{
        paddingLeft: indent,
        background: isSelected ? "rgba(77,184,181,0.08)" : "transparent",
      }}
    >
      <span className="text-[8px] text-slate-300 cursor-grab flex-shrink-0">⠿</span>
      {isNew && <span className="w-[6px] h-[6px] rounded-full bg-green-500 flex-shrink-0 inline-block" title="New" />}
      <span
        className="text-[13px]"
        style={{ cursor: onSelect ? "pointer" : "default" }}
        onClick={onSelect}
      >{icon}</span>
      {editing ? (
        <input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={() => { onRename(node.id, val); setEditing(false); }}
          onKeyDown={e => {
            if (e.key === "Enter") { onRename(node.id, val); setEditing(false); }
            if (e.key === "Escape") { setVal(node.name); setEditing(false); }
          }}
          className="bg-black/[0.04] rounded border px-2 py-[2px] text-slate-800 text-[13px] font-['DM_Sans'] outline-none w-[200px]"
          style={{ borderColor: color }}
        />
      ) : (
        <span
          onClick={onSelect || undefined}
          onDoubleClick={() => setEditing(true)}
          className="text-[13px] text-slate-800 cursor-pointer border-b border-dashed border-black/[0.15] pb-[1px]"
        >{node.name}</span>
      )}
      <span className="text-[10px] font-semibold uppercase tracking-[0.05em] opacity-70" style={{ color }}>
        {["District", "Island", "Building", "Floor", "Room"][level]}
      </span>
      <button
        onClick={() => onDelete(node.id)}
        className="bg-transparent border-none text-red-500 text-[12px] cursor-pointer opacity-50 hover:opacity-100 transition-opacity duration-150 px-1"
      >×</button>
    </div>
  );
}

// ── Isometric preview ──────────────────────────────────────────
const ISO_COLORS = ["#7BA08A", "#8878A0", "#6890A0", "#A89070", "#6B8FA0", "#8A7A60"];
const ISLAND_SHAPES = [
  "M30,60 C20,40 40,20 70,18 C100,16 125,28 135,50 C145,72 132,95 108,102 C84,109 68,105 50,92 C36,82 32,72 30,60Z",
  "M160,55 C152,35 172,15 200,14 C228,13 248,30 252,52 C256,74 240,94 218,98 C196,102 168,96 160,55Z",
  "M20,140 C14,120 30,102 56,99 C82,96 104,110 108,132 C112,154 96,172 72,174 C48,176 26,160 20,140Z",
];
const ISLAND_CENTERS = [{ cx: 82, cy: 60 }, { cx: 206, cy: 56 }, { cx: 64, cy: 136 }];
const BUILDING_DEFAULT_OFFSETS = [{ dx: -22, dy: -18 }, { dx: 8, dy: -22 }, { dx: -28, dy: 2 }, { dx: 10, dy: 0 }];

const ISO_SHAPE_META = [
  { cx: 82,  cy: 60,  w: 125, h: 93 },
  { cx: 206, cy: 56,  w: 104, h: 89 },
  { cx: 64,  cy: 136, w:  98, h: 80 },
];

function seededJitter(id, axis) {
  let h = 0;
  const s = id + axis;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return ((h >>> 0) / 0xFFFFFFFF) * 2 - 1;
}

function IsometricPreview({ world, onPositionsChange, onBuildingClick, selectedBuilding }) {
  const svgRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [positions, setPositions] = useState({});        // { buildingId: { x, y } }
  const [islandPositions, setIslandPositions] = useState({}); // { islandId: { cx, cy } }
  const dragging = useRef(null); // { id, type, startMouse, startPos }
  const didDrag = useRef(false);

  if (!world?.districts?.length) return (
    <div className="flex items-center justify-center h-full text-slate-400 text-[11px] font-['DM_Sans'] text-center p-5">
      Your world will appear here as Lumen builds it.
    </div>
  );

  // Convert SVG client coords
  const toSVG = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const scaleX = 290 / rect.width;
    const scaleY = 220 / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const onBuildingMouseDown = (e, buildingId, defaultX, defaultY, scale = 1) => {
    e.preventDefault();
    e.stopPropagation();
    didDrag.current = false;
    const mouse = toSVG(e.clientX, e.clientY);
    const cur = positions[buildingId] || { x: defaultX, y: defaultY };
    dragging.current = { id: buildingId, type: "building", startMouse: mouse, startPos: cur, scale };
    setHovered(buildingId);
  };

  const onIslandMouseDown = (e, islandId, defaultCx, defaultCy) => {
    e.preventDefault();
    e.stopPropagation();
    const mouse = toSVG(e.clientX, e.clientY);
    const cur = islandPositions[islandId] || { x: defaultCx, y: defaultCy };
    dragging.current = { id: islandId, type: "island", startMouse: mouse, startPos: cur };
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;
    didDrag.current = true;
    const mouse = toSVG(e.clientX, e.clientY);
    const s = dragging.current.type === "building" ? (dragging.current.scale || 1) : 1;
    const dx = (mouse.x - dragging.current.startMouse.x) / s;
    const dy = (mouse.y - dragging.current.startMouse.y) / s;
    const newPos = { x: dragging.current.startPos.x + dx, y: dragging.current.startPos.y + dy };
    if (dragging.current.type === "island") {
      setIslandPositions(prev => ({ ...prev, [dragging.current.id]: newPos }));
    } else {
      setPositions(prev => {
        const updated = { ...prev, [dragging.current.id]: newPos };
        if (onPositionsChange) onPositionsChange(updated);
        return updated;
      });
    }
  };

  const onMouseUp = () => { dragging.current = null; };

  return (
    <svg ref={svgRef} viewBox="0 0 290 220"
      className="w-full h-full overflow-visible"
      style={{ cursor: dragging.current ? "grabbing" : "default" }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
    >
      <defs>
        <radialGradient id="iso_water" cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor="#3A6A8B" />
          <stop offset="100%" stopColor="#1C3D55" />
        </radialGradient>
        <filter id="iso_shadow"><feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.4)" /></filter>
      </defs>
      <rect x="-20" y="-20" width="330" height="260" fill="url(#iso_water)" rx="12" />

      {(() => {
        const sortedIslands = [...world.districts]
          .sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
          .flatMap(d => (d.islands || []).filter(i => i && i.id));
        const n = sortedIslands.length;
        if (!n) return null;
        const cols = Math.ceil(Math.sqrt(n));
        const rows = Math.ceil(n / cols);
        const MARGIN = 20;
        const cellW = (290 - MARGIN * 2) / cols;
        const cellH = (220 - MARGIN * 2) / rows;

        return sortedIslands.map((island, iIdx) => {
          const shape = ISLAND_SHAPES[iIdx % ISLAND_SHAPES.length];
          const meta  = ISO_SHAPE_META[iIdx % ISO_SHAPE_META.length];
          const gridCol = iIdx % cols;
          const gridRow = Math.floor(iIdx / cols);
          const jitterX = seededJitter(island.id, "x") * cellW * 0.12;
          const jitterY = seededJitter(island.id, "y") * cellH * 0.12;
          const defaultGx = MARGIN + gridCol * cellW + cellW / 2 + jitterX;
          const defaultGy = MARGIN + gridRow * cellH + cellH / 2 + jitterY;
          const iPos = islandPositions[island.id] || { x: defaultGx, y: defaultGy };
          const scale = (Math.min(cellW, cellH) * 0.80) / Math.max(meta.w, meta.h);
          const groupTransform = `translate(${iPos.x},${iPos.y}) scale(${scale.toFixed(4)}) translate(${-meta.cx},${-meta.cy})`;
          const buildings = (island.buildings || []);

          return (
            <g key={island.id} transform={groupTransform}>
              <path d={shape} fill="rgba(80,120,100,0.25)" transform="translate(2,3)" />
              <path d={shape} fill="rgba(60,100,80,0.45)" stroke="rgba(255,255,255,0.12)" strokeWidth="1"
                style={{ cursor: "grab" }}
                onMouseDown={e => onIslandMouseDown(e, island.id, defaultGx, defaultGy)} />
              <text x={meta.cx} y={meta.cy + 38} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="6" fontWeight="700" fontFamily="Georgia,serif" letterSpacing="0.4">
                {island.name?.toUpperCase().slice(0, 14)}
              </text>

              {buildings.filter(b => b && b.id).slice(0, 4).map((building, bIdx) => {
                const def = BUILDING_DEFAULT_OFFSETS[bIdx % BUILDING_DEFAULT_OFFSETS.length];
                const defaultX = meta.cx + def.dx;
                const defaultY = meta.cy + def.dy;
                const pos = positions[building.id] || { x: defaultX, y: defaultY };
                const bx = pos.x, by = pos.y;
                const bw = 22, bh = 14;
                const floors = (building.floors || []).length;
                const rooms  = (building.floors || []).reduce((n, f) => n + (f.rooms || []).length, 0);
                const stackH = Math.min(floors * 6 + 8, 28);
                const isHov  = hovered === building.id;
                const isDrag = dragging.current?.id === building.id;
                const col    = ISO_COLORS[bIdx % ISO_COLORS.length];

                return (
                  <g key={building.id}
                    onMouseEnter={() => !dragging.current && setHovered(building.id)}
                    onMouseLeave={() => !dragging.current && setHovered(null)}
                    onMouseDown={e => onBuildingMouseDown(e, building.id, defaultX, defaultY, scale)}
                    onClick={e => { if (!didDrag.current && onBuildingClick) onBuildingClick(building); }}
                    style={{ cursor: isDrag ? "grabbing" : "grab" }}
                    filter="url(#iso_shadow)"
                  >
                    {/* Left face */}
                    <polygon points={`${bx},${by} ${bx},${by+bh} ${bx},${by+bh+stackH} ${bx},${by+stackH}`}
                      fill={isHov || isDrag ? "#4DB8B5" : `${col}88`} />
                    {/* Right face */}
                    <polygon points={`${bx+bw},${by} ${bx+bw},${by+bh} ${bx+bw},${by+bh+stackH} ${bx+bw},${by+stackH}`}
                      fill={isHov || isDrag ? "#3AA8A5" : `${col}55`} />
                    {/* Front face */}
                    <rect x={bx} y={by+stackH} width={bw} height={bh} fill={isHov || isDrag ? "#5DC8C5" : col} rx="1" />
                    {/* Top face */}
                    <rect x={bx} y={by} width={bw} height={stackH} fill={isHov || isDrag ? "#7DE8E5" : `${col}CC`} rx="1" />
                    {/* Windows */}
                    {Array.from({ length: Math.min(rooms, 3) }).map((_, wi) => (
                      <rect key={wi} x={bx+3+wi*7} y={by+stackH+4} width="4" height="4" rx="0.5" fill="rgba(255,255,255,0.25)" />
                    ))}
                    {/* Drag hint dot */}
                    <circle cx={bx+bw/2} cy={by+2} r="2" fill="rgba(255,255,255,0.4)" />
                    {/* Name tooltip */}
                    {(isHov || isDrag) && (
                      <g>
                        <rect x={bx-10} y={by-18} width={bw+20} height={14} rx="3" fill="rgba(0,0,0,0.85)" />
                        <text x={bx+bw/2} y={by-8} textAnchor="middle" fill="white" fontSize="5.5" fontFamily="'DM Sans',sans-serif" fontWeight="600">
                          {building.name?.slice(0, 18)}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </g>
          );
        });
      })()}
    </svg>
  );
}

// ── Lumen review chat ──────────────────────────────────────────
function ReviewLumen({ parsed, onUpdate, pushHistory, userId, reviewSessionId }) {
  const [input, setInput]               = useState("");
  const [thinking, setThinking]         = useState(false);
  const [reviewMessages, setReviewMessages] = useState([]);
  const scrollRef                       = useRef(null);
  const syncRef                         = useRef(null);

  useEffect(() => {
    if (!reviewSessionId || !userId) return;
    const sync = createLumenSessionSync({ userId, roomId: reviewSessionId, kind: "brain_dump" });
    syncRef.current = sync;
    sync.load().then(({ messages }) => {
      if (syncRef.current === sync && messages?.length) setReviewMessages(messages);
    });
  }, [reviewSessionId, userId]);

  // Persist messages to Supabase (debounced 1 s, same pattern as in-room Lumen)
  useEffect(() => {
    const sync = syncRef.current;
    if (!sync || !reviewMessages.length) return;
    const t = setTimeout(async () => {
      const { error } = await sync.save(reviewMessages);
      if (error) console.error("[ReviewLumen] persist error:", error.message);
    }, 1000);
    return () => clearTimeout(t);
  }, [reviewMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom on new messages or thinking state change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [reviewMessages, thinking]);

  const send = async () => {
    if (!input.trim() || thinking) return;
    const userMsg = input.trim();
    setInput("");
    setThinking(true);
    const nextMessages = [...reviewMessages, { role: "user", content: userMsg }];
    setReviewMessages(nextMessages);
    try {
      const result = await ClaudeService.sendStructuredMessage({
        model: "claude-sonnet-4-6",
        maxTokens: 8000,
        system: `You are Lumen inside NeuroQuest. Help the user refine their spatial world hierarchy.

  Current world JSON:
    ${JSON.stringify(parsed, null, 2)}

  Your entire response MUST be a single JSON object only  — nothing before { or after }:

  {
    "chat_response": "Your conversational reply. Never empty. Confirm what changed, explain if nothing changed, or ask for clarification.",
    "updatedWorld": { ...full updated world JSON... }
  }

  If no structural change should be made (for example the user is asking a question, saying "looks good", or clarification is required), set:

  "updatedWorld": null

  ═══════════════════════════════════════
  GENERAL RULES
  ═══════════════════════════════════════

  - Return the COMPLETE world JSON whenever updatedWorld is not null.
  - Preserve every node unless the user explicitly deletes it.
  - Preserve every existing id whenever possible.
  - Never generate replacement ids for existing nodes.
  - When renaming, only modify the "name" field.
  - When adding a brand new node, generate a unique id like:
    - d-new-1
    - i-new-1
    - b-new-1
    - f-new-1
    - r-new-1

  ═══════════════════════════════════════
  EDIT RESOLUTION ORDER (VERY IMPORTANT)
  ═══════════════════════════════════════

  Before creating ANY new node:

  STEP 1
  Search the ENTIRE existing world for an existing node that matches what the user is referring to.

  STEP 2
  If an existing node exists, MODIFY THAT NODE instead of creating another.

  STEP 3
  Only create a new node if no reasonable existing node matches.

  Never skip these steps.

  ═══════════════════════════════════════
  RESTRUCTURING RULES
  ═══════════════════════════════════════

  If the user says things like:

  - move
  - relocate
  - reorganize
  - nest
  - place inside
  - make X part of Y
  - put X under Y
  - convert X into a building inside Y
  - make X belong to Y

  then treat the request as a MOVE operation.

  Do NOT duplicate the node.

  Instead:

  - remove the node from its current parent
  - insert it into the requested parent
  - preserve its existing id
  - preserve all of its children
  - preserve all metadata
  - only change parent references (and name if explicitly requested)

  After the move, the node must exist in exactly ONE location.

  Never leave orphaned duplicates.

  ═══════════════════════════════════════
  RENAME RULES
  ═══════════════════════════════════════

  If the user renames something:

  - keep the same id
  - keep the same children
  - only change "name"

  Never create a replacement node for a rename.

  ═══════════════════════════════════════
  MOVE RULES
  ═══════════════════════════════════════

  When moving:

  - remove from old parent
  - add to new parent
  - preserve id
  - preserve descendants

  This applies to:

  - districts
  - islands
  - buildings
  - floors
  - rooms

  ═══════════════════════════════════════
  AMBIGUITY RULES
  ═══════════════════════════════════════

  If multiple existing nodes could match the user's request:

  DO NOT GUESS.

  Set:

  "updatedWorld": null

  and ask a clarification question.

  Example:

  "I found two buildings named 'Marketing'. Which one did you mean?"

  Never silently move or rename the wrong node.

  ═══════════════════════════════════════
  DELETE RULES
  ═══════════════════════════════════════

  Only delete a node when the user explicitly asks to delete or remove it.

  Never delete nodes as a side effect of another edit.

  ═══════════════════════════════════════
  SAFETY
  ═══════════════════════════════════════

  Never duplicate existing nodes.

  Never leave duplicate copies after a move.

  Never invent replacement ids for existing nodes.

  Always prefer editing an existing node over creating a new one.

  Only create a new node when you are confident it does not already exist.`,
        messages: nextMessages.slice(-10),
      });

      if (result.success) {
        const reply = result.data?.chat_response || "Done.";
        setReviewMessages(prev => [...prev, { role: "assistant", content: reply }]);
        if (result.data?.updatedWorld) {
          pushHistory(parsed);
          onUpdate(result.data.updatedWorld);
        }
      } else {
        setReviewMessages(prev => [...prev, { role: "assistant", content: "Something went wrong. Try again." }]);
      }
    } catch (e) {
      console.error("Review update error:", e);
      setReviewMessages(prev => [...prev, { role: "assistant", content: "Something went wrong. Try again." }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Message history — self-contained scroll box, independent of any ancestor's height */}
      <div ref={scrollRef} style={{ maxHeight: 120, overflowY: "auto", padding: "10px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
        {reviewMessages.length === 0 && !thinking && (
          <div className="text-[12px] text-slate-400 font-['DM_Sans'] leading-[1.6]">
            Tell Lumen what to change — rename, add, move, or remove anything.
          </div>
        )}
        {reviewMessages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "85%",
            background: m.role === "user" ? "#4DB8B5" : "#F1F5F9",
            color: m.role === "user" ? "#0F172A" : "#1E293B",
            borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
            padding: "8px 14px",
            fontSize: 13,
            lineHeight: 1.6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <TypewriterText text={m.content} animate={i === reviewMessages.length - 1 && m.role === "assistant"} />
          </div>
        ))}
        {thinking && (
          <div style={{
            alignSelf: "flex-start",
            background: "#F1F5F9",
            color: "#1E293B",
            borderRadius: "12px 12px 12px 4px",
            padding: "8px 14px",
            fontSize: 13,
            lineHeight: 1.6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <span>Updating your world</span>
            <span style={{ marginLeft: 4, display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>
              <span className="lumen-dot" />
              <span className="lumen-dot" />
              <span className="lumen-dot" />
            </span>
          </div>
        )}
      </div>
      {/* Input bar — pinned to the bottom of the panel, always visible */}
      <div className="flex-shrink-0 border-t border-black/[0.06]">
        <div className="flex items-center gap-2 p-[8px_20px]">
          <span className="text-[11px] text-[#4DB8B5] flex-shrink-0">✦</span>
          <input
            value={input}
            onChange={e => !thinking && setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Make adjustments, just type them here."
            disabled={thinking}
            className="flex-1 bg-black/[0.02] border border-black/10 rounded-lg p-[8px_12px] text-[12px] font-['DM_Sans'] outline-none focus:border-[#4DB8B5]"
            style={{ color: "#1E293B" }}
          />
          <button
            onClick={send}
            disabled={!input.trim() || thinking}
            className="w-8 h-8 rounded-lg border-none flex-shrink-0 text-[15px] flex items-center justify-center"
            style={{
              background: input.trim() && !thinking ? TEAL : "rgba(77,184,181,0.15)",
              color: input.trim() && !thinking ? DARK : "rgba(77,184,181,0.4)",
              cursor: input.trim() && !thinking ? "pointer" : "default",
            }}
          >→</button>
        </div>
        <div className="text-[10px] text-slate-300 font-['DM_Sans'] p-[0_20px_8px] leading-[1.5]">
          e.g. "rename ADA Compliance to Compliance", "add a Legal building", "move Outreach Templates to Research"
        </div>
        </div>
    </div>
  );
}


// ── Floor plan overlay ──────────────────────────────────────────
const ROOM_COLORS = ["#E8F4F1","#EEF0F8","#F0EEF8","#EBF5F0","#F5F0E8","#EDF5F5"];

function FloorPlan({ building }) {
  const floors = building.floors || [];
  if (!floors.length) return <div className="text-slate-400 text-[11px] font-['DM_Sans']">No floors defined.</div>;

  return (
    <div className="flex flex-col gap-2">
      {floors.map((floor, fIdx) => {
        const rooms = floor.rooms || [];
        return (
          <div key={floor.id}>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.06em] mb-1 font-['DM_Sans']">
              {floor.name}
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-1">
              {rooms.map((room, rIdx) => (
                <div
                  key={room.id}
                  className="border border-black/[0.08] rounded-md p-[6px_8px] text-[9px] font-semibold text-slate-700 font-['DM_Sans'] leading-[1.3] text-center min-h-8 flex items-center justify-center"
                  style={{ background: ROOM_COLORS[rIdx % ROOM_COLORS.length] }}
                >
                  {room.name}
                </div>
              ))}
              {rooms.length === 0 && (
                <div className="text-[9px] text-slate-300 font-['DM_Sans'] col-span-full">No rooms</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function countNodes(world) {
  let districts = 0, islands = 0, buildings = 0, floors = 0, rooms = 0;
  for (const d of world.districts || []) {
    districts++;
    for (const i of d.islands || []) {
      islands++;
      for (const b of i.buildings || []) {
        buildings++;
        for (const f of b.floors || []) {
          floors++;
          rooms += (f.rooms || []).length;
        }
      }
    }
  }
  return { districts, islands, buildings, floors, rooms };
}

const BUILD_LINES = [
  "Taking it all in",
  "Sorting what goes where",
  "Building your spaces",
  "Almost there",
];

function LumenLoadingBubble({ isBuilding }) {
  const [lineIndex, setLineIndex] = useState(0);

  useEffect(() => {
    if (!isBuilding) return;
    if (lineIndex >= BUILD_LINES.length - 1) return;
    const t = setTimeout(() => setLineIndex(i => Math.min(i + 1, BUILD_LINES.length - 1)), 2500);
    return () => clearTimeout(t);
  }, [isBuilding, lineIndex]);

  // Reset line index when bubble first mounts as a building bubble
  useEffect(() => {
    if (isBuilding) setLineIndex(0);
  }, [isBuilding]);

  const label = isBuilding ? BUILD_LINES[lineIndex] : "Thinking";

  return (
    <div style={{
      alignSelf: "flex-start",
      maxWidth: "85%",
      background: "#F1F5F9",
      color: "#1E293B",
      borderRadius: "12px 12px 12px 4px",
      padding: "10px 14px",
      fontSize: 13,
      lineHeight: 1.6,
      fontFamily: "'DM Sans', sans-serif",
      minWidth: 120,
    }}>
      <span style={{ transition: "opacity 0.3s ease" }}>{label}</span>
      <span style={{ marginLeft: 4, display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>
        <span className="lumen-dot" />
        <span className="lumen-dot" />
        <span className="lumen-dot" />
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function BrainDumpModal({ onClose }) {
  const { loadParsedWorld, setMapOpen, renameNode, deleteNode, world, getUnityWorldPayload, needsBrainDump, user } = useSpatial();

  const [mode, setMode] = useState("dump");
  const [dumpText, setDumpText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("input");
  const [buildingPositions, setBuildingPositions] = useState({});
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [newNodeIds, setNewNodeIds] = useState(new Set());
  const [isMergeReview, setIsMergeReview] = useState(false);
  const [lumenMessages, setLumenMessages] = useState([]);
  const [reviewSessionId, setReviewSessionId] = useState(null);
  const [guidanceComplete, setGuidanceComplete] = useState(false);
  const [conversationLog, setConversationLog] = useState("");
  const [evalRound, setEvalRound] = useState(0);
  const [isBuilding, setIsBuilding] = useState(false);

  // Undo/redo helpers
  const pushHistory = (prev) => { setHistory(h => [...h.slice(-20), prev]); setFuture([]); };
  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFuture(f => [parsed, ...f]);
    setParsed(prev);
    setHistory(h => h.slice(0, -1));
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setHistory(h => [...h, parsed]);
    setParsed(next);
    setFuture(f => f.slice(1));
  };

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [history, future, parsed]);

  const doParseAndReview = async (messages) => {
    const isMerge = !needsBrainDump && !!(world?.districts?.length);
    const combined = messages
      .filter(m => m.role === "user")
      .map(m => m.content)
      .join("\n\n");
    const flatMessages = [{ role: "user", content: combined }];
    setIsBuilding(true);
    const parseResult = await parseBrainDump(flatMessages, isMerge ? getUnityWorldPayload() : null);
    setIsBuilding(false);
    setParsed(parseResult);
    if (isMerge) {
      const existingIds = new Set();
      const collectIds = (nodes) => nodes?.forEach(n => { existingIds.add(n.id); collectIds(n.islands); collectIds(n.buildings); collectIds(n.floors); collectIds(n.rooms); });
      collectIds(world.districts);
      const newIds = new Set();
      const findNew = (nodes) => nodes?.forEach(n => { if (!existingIds.has(n.id)) newIds.add(n.id); findNew(n.islands); findNew(n.buildings); findNew(n.floors); findNew(n.rooms); });
      findNew(parseResult.districts);
      setNewNodeIds(newIds);
      setIsMergeReview(true);
    } else {
      setNewNodeIds(new Set());
      setIsMergeReview(false);
    }
    setReviewSessionId("__brain_dump_review__" + Date.now());
    setStep("review");
  };

  const handleParse = async () => {
    if (!dumpText.trim()) return;
    setError(null);

    // ── Guided phase: evaluate before building ──
    if (!guidanceComplete) {
      const userText = dumpText.trim();
      const newLog = conversationLog ? conversationLog + "\n\n" + userText : userText;
      setConversationLog(newLog);
      setLumenMessages(prev => [...prev, { role: "user", content: userText }]);
      setDumpText("");
      setLoading(true);
      const newRound = evalRound + 1;
      setEvalRound(newRound);
      try {
        const result = await ClaudeService.sendMessage({
          model: "claude-sonnet-4-6",
          maxTokens: 500,
          system: `You are Lumen, helping a user set up their workspace. You'll receive their brain dump. Your job is to place everything sensibly across the structure: district = broad life area like work or personal, island = a project or company, building = a workstream, room = a specific task.
Place everything you can confidently infer. But you SHOULD ask a clarifying question when something is a genuine coin-flip that changes where it lands. Specifically, ask when you cannot tell:

Whether something is its own separate project, or part of a project they already mentioned (affects island placement).
Whether something is work or personal (affects district placement).
Which project a task belongs to, when it could reasonably belong to more than one.

Do NOT ask about: names, structure, phases, or anything you can reasonably infer from context. Do not ask when both possible answers would land the item in basically the same place.
Thin or ambiguous input: if the content is too sparse to confidently place — a single vague phrase, no clear domain, and it's unclear whether the user means a whole project or a quick note — ask exactly one clarifying question. Name your best guess at where it would land and offer an either/or: "That sounds like it could live under [X]. Want to add a bit more detail first, or should I just build it as-is?" One question, two options, done. Do not list multiple interpretations or ask follow-ups before they respond.
If everything places confidently with no genuine coin-flips, respond with exactly READY_TO_BUILD on its own line, nothing else.
If there are genuine coin-flips, ask them ALL AT ONCE in one message. Open with a short warm line like 'Just need to clarify a couple things:' then a numbered list. Ask at most 2 questions, fewer if you can. One short line each.
Never use em dashes. Write around them. BAD: "Is the brand work separate from the agency, or part of it?" with an em dash inserted. GOOD: "Is your brand work its own project, or part of the agency project?"
Close the message with one short warm line nudging them to answer, woven into the same message. Example of a complete clarifying turn:
Just need to clarify a couple things:
1. Is the rebrand its own project, or part of your agency work?
2. Is the fitness goal personal, or tied to a work thing like a company wellness program?
Give me a little more context and I'll build it out.
After the user answers, place everything and respond with READY_TO_BUILD on its own line. Never go more than two rounds total. The user reviews everything visually next and can adjust, so once you have the answers, build.`,
          messages: [
            ...lumenMessages.filter(m => m.role === "user" || m.role === "assistant"),
            { role: "user", content: userText },
          ],
        });
        if (!result.success) throw new Error(result.error);
        const reply = (result.content || "").trim();
        if (reply.includes("READY_TO_BUILD") || newRound >= 2) {
          setGuidanceComplete(true);
          await doParseAndReview([...lumenMessages, { role: "user", content: userText }]);
        } else {
          setLumenMessages(prev => [...prev, { role: "assistant", content: reply }]);
          setConversationLog(prev => prev + "\n\nLumen: " + reply);
        }
      } catch (e) {
        console.error("Guidance failed:", e);
        setError("Lumen had trouble with that. Try again or switch to manual mode.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // ── Build phase: guidance already complete ──
    setLoading(true);
    try {
      await doParseAndReview(
        lumenMessages.length > 0
          ? lumenMessages
          : [{ role: "user", content: dumpText.trim() }]
      );
    } catch (e) {
      console.error("Brain dump parse failed:", e);
      const detail = e?.message || "";
      if (/api key|not configured|edge function/i.test(detail)) {
        setError("Brain Dump AI is not configured. Check REACT_APP_CLAUDE_API_KEY in .env.");
      } else {
        setError("Lumen had trouble parsing that. Try being a bit more specific, or switch to manual mode.");
      }
    } finally {
      setLoading(false);
    }
  };

  const seedParsedFromWorld = () => {
    if (!world?.districts?.length) return null;
    return {
      id: world.id || "world-1",
      name: world.name || "My World",
      districts: JSON.parse(JSON.stringify(world.districts)),
      surface_items: world.surface_items ? JSON.parse(JSON.stringify(world.surface_items)) : [],
    };
  };

  /** Preserve parsed world when switching tabs — only step/mode changes, no wipe. */
  const handleModeSwitch = (nextMode) => {
    if (nextMode === mode) return;
    if (nextMode === "manual") {
      setParsed((prev) => {
        if (prev?.districts?.length) return prev;
        return seedParsedFromWorld() || prev;
      });
      setMode("manual");
      return;
    }
    setMode("dump");
    setStep(parsed?.districts?.length ? "review" : "input");
  };

  const handleGoSpatial = async (worldOverride) => {
    // worldOverride must be a plain object with districts, not a click event
    const isValidWorld = worldOverride && typeof worldOverride === "object" && Array.isArray(worldOverride.districts);
    const target = isValidWorld ? worldOverride : parsed;
    if (!target) return;
    // Merge drag positions into world JSON before loading
    const mergePositions = (districts) => (districts || []).map(d => ({
      ...d,
      islands: (d.islands || []).map(i => ({
        ...i,
        buildings: (i.buildings || []).map(b => ({
          ...b,
          previewPosition: buildingPositions[b.id] || null,
        })),
      })),
    }));
    const worldWithPositions = { ...target, districts: mergePositions(target.districts), surface_items: target.surface_items || parsed?.surface_items || [] };
    const result = await loadParsedWorld(worldWithPositions);
    if (result?.ok === false) {
      setError(result.error || "Could not save world to Supabase. Open browser console for details.");
      return;
    }
    onClose();
    setMapOpen(true);
  };

  const handleRename = (id, name) => {
    if (!parsed) return;
    pushHistory(parsed);
    const rename = (nodes) => nodes.map(n => {
      if (n.id === id) return { ...n, name };
      return {
        ...n,
        islands: n.islands ? rename(n.islands) : undefined,
        buildings: n.buildings ? rename(n.buildings) : undefined,
        floors: n.floors ? rename(n.floors) : undefined,
        rooms: n.rooms ? rename(n.rooms) : undefined,
      };
    });
    setParsed(prev => ({ ...prev, districts: rename(prev.districts) }));
  };

  const handleReorder = (parentId, dragIdx, dropIdx, level) => {
    if (!parsed || dragIdx === dropIdx) return;
    pushHistory(parsed);
    const reorder = (nodes) => nodes.map(n => {
      // Check if this node is the parent
      const childKey = ["islands","buildings","floors","rooms"][level];
      if (n.id === parentId && n[childKey]) {
        const arr = [...n[childKey]];
        const [moved] = arr.splice(dragIdx, 1);
        arr.splice(dropIdx, 0, moved);
        return { ...n, [childKey]: arr };
      }
      return {
        ...n,
        islands: n.islands ? reorder(n.islands) : undefined,
        buildings: n.buildings ? reorder(n.buildings) : undefined,
        floors: n.floors ? reorder(n.floors) : undefined,
        rooms: n.rooms ? reorder(n.rooms) : undefined,
      };
    });
    // Also handle top-level districts reorder
    if (level === -1 && parentId === "root") {
      const arr = [...parsed.districts];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(dropIdx, 0, moved);
      setParsed(prev => ({ ...prev, districts: arr }));
    } else {
      setParsed(prev => ({ ...prev, districts: reorder(prev.districts) }));
    }
  };

  const handleCrossIslandMove = (buildingId, sourceIslandId, targetIslandId) => {
    if (!parsed || sourceIslandId === targetIslandId) return;
    pushHistory(parsed);
    let movedBuilding = null;
    const afterRemove = parsed.districts.map(d => ({
      ...d,
      islands: (d.islands || []).map(island => {
        if (island.id !== sourceIslandId) return island;
        movedBuilding = (island.buildings || []).find(b => b.id === buildingId) ?? null;
        return { ...island, buildings: (island.buildings || []).filter(b => b.id !== buildingId) };
      }),
    }));
    if (!movedBuilding) return;
    const afterInsert = afterRemove.map(d => ({
      ...d,
      islands: (d.islands || []).map(island => {
        if (island.id !== targetIslandId) return island;
        return { ...island, buildings: [...(island.buildings || []), movedBuilding] };
      }),
    }));
    setParsed(prev => ({ ...prev, districts: afterInsert }));
  };

  const handleDelete = (id) => {
    if (!parsed) return;
    pushHistory(parsed);
    const remove = (nodes) => nodes
      .filter(n => n.id !== id)
      .map(n => ({
        ...n,
        islands: n.islands ? remove(n.islands) : undefined,
        buildings: n.buildings ? remove(n.buildings) : undefined,
        floors: n.floors ? remove(n.floors) : undefined,
        rooms: n.rooms ? remove(n.rooms) : undefined,
      }));
    setParsed(prev => ({ ...prev, districts: remove(prev.districts) }));
  };

  const counts = parsed ? countNodes(parsed) : null;
  const hasWorld = world.districts.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-lg z-[100] flex items-center justify-center"
    >
      <div
        className="bg-white border border-black/[0.08] rounded-[20px] flex flex-col overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)] transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          width: (step === "review" || mode === "manual") ? "min(1100px, 95vw)" : "min(700px, 90vw)",
          maxHeight: "90vh",
        }}
      >

        {/* Header */}
        <div className="flex items-center justify-between p-[20px_24px] border-b border-black/[0.04]">
          <div>
            <div className="text-[18px] font-bold text-slate-800 font-['DM_Sans']">
              {step === "input" ? (hasWorld ? "Add to Your World" : "Build Your World") : "Review Your World"}
            </div>
            <div className="text-[12px] text-slate-500 mt-[2px] font-['DM_Sans']">
              {step === "input"
                ? (hasWorld ? "Dump what's new — Lumen will merge it into your existing world." : "Brain dump everything on your plate, or build manually.")
                : "Edit anything before going spatial."}
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-black/[0.04] border border-black/10 rounded-lg p-[6px_14px] text-slate-500 text-[12px] cursor-pointer font-['DM_Sans']"
          >✕ Close</button>
        </div>

        {/* Mode tabs - always visible */}
        <div className="flex gap-1 p-[12px_24px] border-b border-black/[0.04]">
          {[
            { id: "dump", label: "✦ Brain Dump", desc: "Let Lumen build your world" },
            { id: "manual", label: "⊞ Manual", desc: "Build it yourself" },
          ].map(m => (
            <button
              key={m.id}
              onClick={() => handleModeSwitch(m.id)}
              className="p-[8px_18px] rounded-lg text-[12px] font-semibold cursor-pointer font-['DM_Sans'] transition-all duration-150"
              style={{
                background: mode === m.id ? "rgba(77,184,181,0.15)" : "rgba(0,0,0,0.02)",
                border: `1px solid ${mode === m.id ? "rgba(77,184,181,0.4)" : "rgba(0,0,0,0.06)"}`,
                color: mode === m.id ? TEAL : "#64748B",
              }}
            >{m.label}</button>
          ))}
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-auto"
          style={{ padding: step === "review" ? "0" : "20px 24px" }}
        >
          <style>{`
            @keyframes lumen-dot-pulse {
              0%, 80%, 100% { opacity: 0.2; }
              40% { opacity: 1; }
            }
            .lumen-dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: #94A3B8; margin: 0 2px; animation: lumen-dot-pulse 1.4s ease-in-out infinite; }
            .lumen-dot:nth-child(2) { animation-delay: 0.2s; }
            .lumen-dot:nth-child(3) { animation-delay: 0.4s; }
          `}</style>

          {/* ── BRAIN DUMP MODE ── */}
          {mode === "dump" && step === "input" && (
            <div className="flex flex-col gap-4">
              {lumenMessages.length === 0 && !loading && (
                <div className="text-[13px] text-slate-500 font-['DM_Sans'] leading-[1.6]">
                  Just write. Don't organize. Tell Lumen everything on your plate - projects, tasks, ideas, goals. Lumen will turn it into your world.
                </div>
              )}
              {(lumenMessages.length > 0 || loading) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {lumenMessages.map((m, i) => (
                    <div key={i} style={{
                      alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: "85%",
                      background: m.role === "user" ? "#4DB8B5" : "#F1F5F9",
                      color: m.role === "user" ? "#0F172A" : "#1E293B",
                      borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                      padding: "10px 14px",
                      fontSize: 13,
                      lineHeight: 1.6,
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      <TypewriterText text={m.content} animate={i === lumenMessages.length - 1 && m.role === "assistant"} />
                    </div>
                  ))}
                  {loading && <LumenLoadingBubble isBuilding={isBuilding} />}
                </div>
              )}
              <textarea
                value={dumpText}
                onChange={e => setDumpText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (dumpText.trim() && !loading) handleParse();
                  }
                }}
                placeholder={lumenMessages.length > 0 ? "Reply to Lumen..." : "I'm launching a new product and need to finish the landing page. Have a client meeting Thursday. Also want to get back to the gym and finally organize my inbox..."}
                autoFocus
                className="bg-black/[0.02] border border-black/10 rounded-xl p-4 text-slate-800 text-[13px] leading-[1.7] font-['DM_Sans'] resize-none min-h-[200px] outline-none w-full box-border focus:border-[#4DB8B5]"
              />
              {error && (
                <div className="text-[12px] text-red-500 font-['DM_Sans']">{error}</div>
              )}
            </div>
          )}

          {/* ── REVIEW MODE ── */}
          {mode === "dump" && step === "review" && parsed && (
            <div className="flex flex-col h-full min-h-[480px]">

              {/* TOP — tree left, preview right */}
              <div className="flex-[3] flex overflow-hidden">

                {/* LEFT — hierarchy tree */}
                <div className="flex-1 overflow-y-auto p-[16px_16px_16px_20px] border-r border-black/[0.06]">
                  <div className="bg-[#4DB8B5]/[0.08] border border-[#4DB8B5]/[0.2] rounded-[10px] p-[10px_14px] mb-4 text-[12px] text-slate-500 font-['DM_Sans'] leading-[1.6]">
                    <div className="flex items-center justify-between gap-2">
                      <span>{isMergeReview ? "New content to add — " : "Lumen found "}
                      <span style={{ color: TEAL }} className="font-semibold">{counts.districts}d</span>, <span className="text-purple-400 font-semibold">{counts.islands} island{counts.islands !== 1 ? "s" : ""}</span>, <span className="text-blue-500 font-semibold">{counts.buildings} building{counts.buildings !== 1 ? "s" : ""}</span>, <span className="text-green-500 font-semibold">{counts.rooms} room{counts.rooms !== 1 ? "s" : ""}</span>.
                      {isMergeReview ? " Green dots = new. Approve to merge." : " Click to rename. × to remove."}
                    </span>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={undo}
                          disabled={!history.length}
                          title="Undo (Cmd+Z)"
                          className="p-[3px_8px] rounded-[5px] border border-black/10 text-[11px] font-['DM_Sans']"
                          style={{
                            background: history.length ? "white" : "transparent",
                            color: history.length ? "#334155" : "#CBD5E1",
                            cursor: history.length ? "pointer" : "default",
                          }}
                        >↩ Undo</button>
                        <button
                          onClick={redo}
                          disabled={!future.length}
                          title="Redo (Cmd+Shift+Z)"
                          className="p-[3px_8px] rounded-[5px] border border-black/10 text-[11px] font-['DM_Sans']"
                          style={{
                            background: future.length ? "white" : "transparent",
                            color: future.length ? "#334155" : "#CBD5E1",
                            cursor: future.length ? "pointer" : "default",
                          }}
                        >↪ Redo</button>
                      </div>
                    </div>
                  </div>
                  <HierarchyText world={parsed} onRename={handleRename} onDelete={handleDelete} onReorder={handleReorder} onSelectBuilding={setSelectedBuilding} selectedBuilding={selectedBuilding} isMerge={isMergeReview} newNodeIds={newNodeIds} />
                </div>

                {/* RIGHT — floor plan + isometric preview */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Floor plan panel - shown when building selected */}
                  {selectedBuilding && (
                    <div className="flex-shrink-0 border-b border-black/[0.06] p-[10px_14px] max-h-[55%] overflow-y-auto">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold text-slate-800 font-['DM_Sans']">🏢 {selectedBuilding.name}</span>
                        <button
                          onClick={() => setSelectedBuilding(null)}
                          className="bg-transparent border-none text-slate-300 text-[13px] cursor-pointer"
                        >✕</button>
                      </div>
                      <FloorPlan key={selectedBuilding.id} building={selectedBuilding} />
                    </div>
                  )}
                  {/* Isometric preview */}
                  <div className="flex-1 p-3 flex items-center justify-center min-h-0">
                    <IsometricPreview world={parsed} onPositionsChange={setBuildingPositions} selectedBuilding={selectedBuilding} onBuildingClick={(b) => {
                      const fullBuilding = parsed.districts.flatMap(d => d.islands || []).flatMap(i => i.buildings || []).find(bld => bld.id === b.id);
                      setSelectedBuilding(prev => prev?.id === b.id ? null : (fullBuilding || b));
                    }} />
                  </div>
                  <div className="p-[4px_14px_6px] flex-shrink-0">
                    <span className="text-[9px] text-slate-300 font-['DM_Sans']">Click a building to see floor plan</span>
                  </div>
                </div>

              </div>

            </div>
          )}

          {/* ── MANUAL MODE ── */}
          {mode === "manual" && (
            <ManualBuilder parsed={parsed} setParsed={setParsed} pushHistory={pushHistory} onGoSpatial={handleGoSpatial} />
          )}
        </div>

        <div>
        {/* BOTTOM — Lumen review chat */}

        {
          mode === "dump" && step === "review" && parsed && (
            <div className="flex-shrink-0 border-t border-black/[0.06]">
              <ReviewLumen parsed={parsed} onUpdate={setParsed} pushHistory={pushHistory} userId={user?.id} reviewSessionId={reviewSessionId} />
            </div>
          )
        }


        {/* Footer actions */}
          <div className="p-[16px_24px] border-t border-black/[0.04] flex justify-between items-center">
            {step === "review" && (
              <button
                onClick={() => { setStep("input"); setReviewSessionId(null); }}
                className="bg-transparent border-none text-slate-500 text-[12px] cursor-pointer font-['DM_Sans']"
              >← Start over</button>
            )}
            {step === "input" && <div />}

            <div className="flex gap-2">
              {mode === "dump" && step === "input" && (
                <button
                  onClick={handleParse}
                  disabled={!dumpText.trim() || loading}
                  className="p-[10px_24px] rounded-[10px] border-none text-[13px] font-bold cursor-pointer font-['DM_Sans'] transition-all duration-200"
                  style={{
                    background: dumpText.trim() && !loading ? TEAL : "rgba(77,184,181,0.2)",
                    color: dumpText.trim() && !loading ? DARK : "rgba(77,184,181,0.4)",
                    cursor: dumpText.trim() && !loading ? "pointer" : "default",
                  }}
                >
                  {loading ? "Lumen is thinking..." : "Send to Lumen →"}
                </button>
              )}
              {step === "review" && (
                <button
                  onClick={handleGoSpatial}
                  className="p-[10px_28px] rounded-[10px] border-none text-[14px] font-extrabold cursor-pointer font-['DM_Sans'] shadow-[0_4px_20px_rgba(77,184,181,0.4)] transition-all duration-200 tracking-[0.02em] hover:scale-[1.02]"
                  style={{
                    background: `linear-gradient(135deg, ${TEAL}, #38B2AC)`,
                    color: DARK,
                  }}
                >
                  🌍 Go Spatial
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Manual Builder ─────────────────────────────────────────────
function genId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 7)}`;
}

const LEVEL_META = [
  { key: "district", label: "District", icon: "🌍", color: "#4DB8B5",  hint: "A broad life area — Work, Personal, Health, Finance" },
  { key: "island",   label: "Island",   icon: "🏝",  color: "#A78BFA",  hint: "A project or company — a startup, a side business, a creative project" },
  { key: "building", label: "Building", icon: "🏢",  color: "#3B82F6",  hint: "A workstream — Beta Launch, Fundraising, Legal" },
  { key: "floor",    label: "Floor",    icon: "▤",   color: "#22C55E",  hint: "A phase — Planning, Active Build, Research" },
  { key: "room",     label: "Room",     icon: "🚪",  color: "#F59E0B",  hint: "A specific workspace — Pitch Deck, Patent Filing, App Cleanup" },
];

function ManualBuilder({ parsed, setParsed, pushHistory, onGoSpatial }) {
  const [selectedLevel, setSelectedLevel] = useState("district");
  const [inputVal, setInputVal] = useState("");
  const [parentPath, setParentPath] = useState([]); // [districtId, islandId, buildingId, floorId]
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const inputRef = useRef(null);

  const world = parsed || { id: "world-1", name: "My World", districts: [] };

  // Derive available parents for current level
  const parentOptions = () => {
    if (selectedLevel === "district") return null; // no parent needed
    if (selectedLevel === "island") {
      return world.districts.map(d => ({ id: d.id, label: d.name, level: "district" }));
    }
    if (selectedLevel === "building") {
      return world.districts.flatMap(d => (d.islands || []).map(i => ({ id: i.id, label: `${d.name} › ${i.name}`, level: "island", districtId: d.id })));
    }
    if (selectedLevel === "floor") {
      return world.districts.flatMap(d => (d.islands || []).flatMap(i => (i.buildings || []).map(b => ({ id: b.id, label: `${i.name} › ${b.name}`, level: "building", districtId: d.id, islandId: i.id }))));
    }
    if (selectedLevel === "room") {
      return world.districts.flatMap(d => (d.islands || []).flatMap(i => (i.buildings || []).flatMap(b => (b.floors || []).map(f => ({ id: f.id, label: `${b.name} › ${f.name}`, level: "floor", districtId: d.id, islandId: i.id, buildingId: b.id })))));
    }
    return [];
  };

  const [parentId, setParentId] = useState("");

  // Reset parentId when level changes
  useEffect(() => {
    setParentId("");
    setInputVal("");
    inputRef.current?.focus();
  }, [selectedLevel]);

  const handleAdd = () => {
    if (!inputVal.trim()) return;
    const name = inputVal.trim();
    pushHistory(world);

    let updated = { ...world };

    if (selectedLevel === "district") {
      updated = { ...world, districts: [...world.districts, { id: genId("d"), name, islands: [] }] };
    } else if (selectedLevel === "island") {
      updated = { ...world, districts: world.districts.map(d => d.id !== parentId ? d : { ...d, islands: [...(d.islands || []), { id: genId("i"), name, buildings: [] }] }) };
    } else if (selectedLevel === "building") {
      const opts = parentOptions();
      const par = opts?.find(o => o.id === parentId);
      updated = { ...world, districts: world.districts.map(d => d.id !== par?.districtId ? d : { ...d, islands: (d.islands || []).map(i => i.id !== parentId ? i : { ...i, buildings: [...(i.buildings || []), { id: genId("b"), name, floors: [] }] }) }) };
    } else if (selectedLevel === "floor") {
      const opts = parentOptions();
      const par = opts?.find(o => o.id === parentId);
      updated = { ...world, districts: world.districts.map(d => d.id !== par?.districtId ? d : { ...d, islands: (d.islands || []).map(i => i.id !== par?.islandId ? i : { ...i, buildings: (i.buildings || []).map(b => b.id !== parentId ? b : { ...b, floors: [...(b.floors || []), { id: genId("f"), name, rooms: [] }] }) }) }) };
    } else if (selectedLevel === "room") {
      const opts = parentOptions();
      const par = opts?.find(o => o.id === parentId);
      updated = { ...world, districts: world.districts.map(d => d.id !== par?.districtId ? d : { ...d, islands: (d.islands || []).map(i => i.id !== par?.islandId ? i : { ...i, buildings: (i.buildings || []).map(b => b.id !== par?.buildingId ? b : { ...b, floors: (b.floors || []).map(f => f.id !== parentId ? f : { ...f, rooms: [...(f.rooms || []), { id: genId("r"), name }] }) }) }) }) };
    }

    setParsed(updated);
    setInputVal("");
    inputRef.current?.focus();

    // Auto-advance level if there are now enough nodes to add the next level
    const levelOrder = ["district","island","building","floor","room"];
    const nextIdx = levelOrder.indexOf(selectedLevel) + 1;
    if (nextIdx < levelOrder.length) setSelectedLevel(levelOrder[nextIdx]);
  };

  const handleRename = (id, name) => {
    pushHistory(world);
    const rename = (nodes) => nodes.map(n => {
      if (n.id === id) return { ...n, name };
      return { ...n, islands: n.islands ? rename(n.islands) : undefined, buildings: n.buildings ? rename(n.buildings) : undefined, floors: n.floors ? rename(n.floors) : undefined, rooms: n.rooms ? rename(n.rooms) : undefined };
    });
    setParsed(prev => ({ ...prev, districts: rename(prev.districts) }));
  };

  const handleDelete = (id) => {
    pushHistory(world);
    const remove = (nodes) => nodes.filter(n => n.id !== id).map(n => ({ ...n, islands: n.islands ? remove(n.islands) : undefined, buildings: n.buildings ? remove(n.buildings) : undefined, floors: n.floors ? remove(n.floors) : undefined, rooms: n.rooms ? remove(n.rooms) : undefined }));
    setParsed(prev => ({ ...prev, districts: remove(prev.districts) }));
  };

  const handleReorder = (parentId, dragIdx, dropIdx, level) => {
    if (!world.districts.length || dragIdx === dropIdx) return;
    pushHistory(world);
    const reorder = (nodes) => nodes.map(n => {
      const childKey = ["islands", "buildings", "floors", "rooms"][level];
      if (n.id === parentId && n[childKey]) {
        const arr = [...n[childKey]];
        const [moved] = arr.splice(dragIdx, 1);
        arr.splice(dropIdx, 0, moved);
        return { ...n, [childKey]: arr };
      }
      return { ...n, islands: n.islands ? reorder(n.islands) : undefined, buildings: n.buildings ? reorder(n.buildings) : undefined, floors: n.floors ? reorder(n.floors) : undefined, rooms: n.rooms ? reorder(n.rooms) : undefined };
    });
    if (level === -1 && parentId === "root") {
      const arr = [...world.districts];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(dropIdx, 0, moved);
      setParsed(prev => ({ ...(prev || world), districts: arr }));
    } else {
      setParsed(prev => ({ ...(prev || world), districts: reorder((prev || world).districts) }));
    }
  };

  const handleCrossIslandMove = (buildingId, sourceIslandId, targetIslandId) => {
    if (!world.districts.length || sourceIslandId === targetIslandId) return;
    pushHistory(world);
    let movedBuilding = null;
    const afterRemove = world.districts.map(d => ({
      ...d,
      islands: (d.islands || []).map(island => {
        if (island.id !== sourceIslandId) return island;
        movedBuilding = (island.buildings || []).find(b => b.id === buildingId) ?? null;
        return { ...island, buildings: (island.buildings || []).filter(b => b.id !== buildingId) };
      }),
    }));
    if (!movedBuilding) return;
    const afterInsert = afterRemove.map(d => ({
      ...d,
      islands: (d.islands || []).map(island => {
        if (island.id !== targetIslandId) return island;
        return { ...island, buildings: [...(island.buildings || []), movedBuilding] };
      }),
    }));
    setParsed(prev => ({ ...(prev || world), districts: afterInsert }));
  };

  const options = parentOptions();
  const meta = LEVEL_META.find(m => m.key === selectedLevel);
  const hasWorld = world.districts.length > 0;
  const needsParent = selectedLevel !== "district";
  const canAdd = inputVal.trim() && (!needsParent || parentId);

  return (
    <div className="flex h-full min-h-[480px] overflow-hidden">

      {/* LEFT — builder controls + tree */}
      <div className="flex-1 flex flex-col border-r border-black/[0.06] overflow-hidden">

        {/* Level selector */}
        <div className="flex-shrink-0 p-[16px_20px_0]">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.06em] mb-2">Add a</div>
          <div className="flex gap-[6px] flex-wrap">
            {LEVEL_META.map(lm => (
              <button
                key={lm.key}
                onClick={() => setSelectedLevel(lm.key)}
                className="p-[5px_12px] rounded-full text-[11px] font-semibold cursor-pointer font-['DM_Sans'] transition-all duration-150"
                style={{
                  background: selectedLevel === lm.key ? lm.color + "20" : "rgba(0,0,0,0.03)",
                  border: `1.5px solid ${selectedLevel === lm.key ? lm.color : "rgba(0,0,0,0.08)"}`,
                  color: selectedLevel === lm.key ? lm.color : "#64748B",
                }}
              >
                {lm.icon} {lm.label}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-[6px] font-['DM_Sans']">{meta.hint}</div>
        </div>

        {/* Parent selector (if needed) */}
        {needsParent && (
          <div className="flex-shrink-0 p-[12px_20px_0]">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.06em] mb-[6px]">Inside</div>
            {options && options.length > 0 ? (
              <select
                value={parentId}
                onChange={e => setParentId(e.target.value)}
                className="w-full p-[7px_10px] rounded-[7px] border border-slate-200 bg-slate-50 text-[12px] text-slate-800 font-['DM_Sans'] outline-none cursor-pointer"
              >
                <option value="">— choose one —</option>
                {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            ) : (
              <div className="text-[12px] text-red-500 font-['DM_Sans']">
                Add a {LEVEL_META[LEVEL_META.findIndex(m => m.key === selectedLevel) - 1]?.label.toLowerCase()} first.
              </div>
            )}
          </div>
        )}

        {/* Name input */}
        <div className="flex-shrink-0 p-[12px_20px_16px] flex gap-2">
          <input
            ref={inputRef}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => e.key === "Enter" && canAdd && handleAdd()}
            placeholder={`${meta.icon} ${meta.label} name...`}
            className="flex-1 p-[8px_12px] rounded-lg bg-slate-50 text-[13px] text-slate-800 font-['DM_Sans'] outline-none transition-[border-color] duration-150"
            style={{ border: `1.5px solid ${canAdd ? meta.color : "#E2E8F0"}` }}
          />
          <button
            onClick={handleAdd}
            disabled={!canAdd}
            className="p-[8px_18px] rounded-lg border-none text-[12px] font-bold cursor-pointer font-['DM_Sans'] transition-all duration-150 flex-shrink-0"
            style={{
              background: canAdd ? meta.color : "rgba(0,0,0,0.06)",
              color: canAdd ? "white" : "#CBD5E1",
              cursor: canAdd ? "pointer" : "default",
            }}
          >
            + Add
          </button>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-y-auto p-[0_12px_16px]">
          {!hasWorld ? (
            <div className="text-center p-[32px_20px] text-slate-300 text-[12px] font-['DM_Sans']">
              <div className="text-[28px] mb-2">🌍</div>
              Start with a District — a broad area of your life.
            </div>
          ) : (
            <HierarchyText world={world} onRename={handleRename} onDelete={handleDelete} onReorder={handleReorder} onSelectBuilding={setSelectedBuilding} selectedBuilding={selectedBuilding} isMerge={false} newNodeIds={new Set()} />
          )}
        </div>

        {/* Go Spatial */}
        {hasWorld && (
          <div className="flex-shrink-0 p-[12px_20px_16px] border-t border-black/[0.04]">
            <button
              onClick={() => onGoSpatial(world)}
              className="w-full p-[11px] rounded-[10px] border-none text-[14px] font-extrabold cursor-pointer font-['DM_Sans'] shadow-[0_4px_20px_rgba(77,184,181,0.4)] tracking-[0.02em] hover:scale-[1.01]"
              style={{
                background: `linear-gradient(135deg, ${TEAL}, #38B2AC)`,
                color: DARK,
              }}
            >
              🌍 Go Spatial
            </button>
          </div>
        )}
      </div>

      {/* RIGHT — isometric preview */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedBuilding && (
          <div className="flex-shrink-0 border-b border-black/[0.06] p-[10px_14px] max-h-[45%] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-slate-800 font-['DM_Sans']">🏢 {selectedBuilding.name}</span>
              <button
                onClick={() => setSelectedBuilding(null)}
                className="bg-transparent border-none text-slate-300 text-[13px] cursor-pointer"
              >✕</button>
            </div>
            <FloorPlan building={selectedBuilding} />
          </div>
        )}
        <div className="flex-1 p-3 flex items-center justify-center min-h-0">
          <IsometricPreview world={world} onPositionsChange={() => {}} selectedBuilding={selectedBuilding}
            onBuildingClick={(b) => {
              const full = world.districts.flatMap(d => d.islands || []).flatMap(i => i.buildings || []).find(bld => bld.id === b.id);
              setSelectedBuilding(prev => prev?.id === b.id ? null : (full || b));
            }} />
        </div>
        <div className="p-[4px_14px_8px] flex-shrink-0">
          <span className="text-[9px] text-slate-300 font-['DM_Sans']">Click a building to see floor plan</span>
        </div>
      </div>

    </div>
  );
}
